import { RefObject, useCallback, useEffect, useRef, useState } from "react";

/** Motion primitives for the landing.
 *
 *  Scrolling carries the visitor from scene to scene; it does not drive the
 *  scenes frame by frame. A scene plays on its own once it is on screen: a
 *  timeline adds its step classes (`s1`, `s2`, ...) at set times, and the CSS
 *  transitions between states with its own curves and delays. Leaving the
 *  scene resets it, so coming back plays it again. Only the entrance into the
 *  product is tied to the scroll, through `useScrollScene`. */

/** Local progress of a sub-range: 0 before `a`, 1 after `b`, linear between. */
export function span(p: number, a: number, b: number): number {
  return Math.min(1, Math.max(0, (p - a) / (b - a)));
}

export function reducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Hands `onProgress` the scroll progress of `ref` through its own height.
 *
 *  Progress is 0 when the scene's top reaches the top of the viewport and 1
 *  when its bottom reaches the bottom, which is exactly the stretch its
 *  sticky child stays pinned. */
export function useScrollScene<T extends HTMLElement>(
  ref: RefObject<T>,
  onProgress: (p: number) => void,
): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frame = 0;
    const write = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      /* The pinned child's own height, not innerHeight: on phones the two
         differ by the address bar, and progress must end when the pin lets go. */
      const pinned = (el.firstElementChild as HTMLElement | null)?.offsetHeight ?? window.innerHeight;
      const travel = rect.height - pinned;
      onProgress(travel > 0 ? span(-rect.top, 0, travel) : 1);
    };
    const request = () => {
      if (!frame) frame = requestAnimationFrame(write);
    };
    write();
    window.addEventListener("scroll", request, { passive: true });
    window.addEventListener("resize", request);
    return () => {
      window.removeEventListener("scroll", request);
      window.removeEventListener("resize", request);
      cancelAnimationFrame(frame);
    };
  }, [ref, onProgress]);
}

/** Plays a scene's steps on `ref` while it is in view.
 *
 *  `at` holds the time in milliseconds at which each step's class is added:
 *  `at[0]` adds `s1`, `at[1]` adds `s2`, and so on. `on` is added on entry,
 *  for the ambient life that runs while the scene is visible. Leaving the view
 *  removes every class, so the scene is ready to play again. With reduced
 *  motion every step is applied at once and the scene rests in its final
 *  state. `start` lets the caller hold the timeline (scene 1 waits until the
 *  visitor is inside the product). `loop`, when set, clears the steps that
 *  long after the last one and plays them again, for a scene that keeps
 *  working while it is watched. */
export function useTimeline<T extends HTMLElement>(
  ref: RefObject<T>,
  at: number[],
  { threshold = 0.35, start = true, loop }: { threshold?: number; start?: boolean; loop?: number } = {},
): void {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }
    /* In view means `threshold` of the scene is showing, or it covers that
       much of the screen (a scene taller than the screen never shows 35% of
       itself). It only counts as gone once it is entirely off screen, so a
       scene is not reset while the visitor is still looking at part of it. */
    const steps = Array.from({ length: 21 }, (_, i) => i / 20);
    const io = new IntersectionObserver(
      ([entry]) => {
        const covered = entry.intersectionRect.height / window.innerHeight;
        if (entry.isIntersecting && (entry.intersectionRatio >= threshold || covered >= threshold)) setVisible(true);
        else if (!entry.isIntersecting) setVisible(false);
      },
      { threshold: steps },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [ref, threshold]);

  const running = visible && start;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const steps = at.map((_, i) => `s${i + 1}`);
    const clear = () => el.classList.remove("on", ...steps);
    if (!running) {
      clear();
      return;
    }
    el.classList.add("on");
    if (reducedMotion()) {
      el.classList.add(...steps);
      return clear;
    }
    let timers: number[] = [];
    const play = () => {
      timers = at.map((ms, i) => window.setTimeout(() => el.classList.add(steps[i]), ms));
      if (loop !== undefined) {
        timers.push(
          window.setTimeout(() => {
            el.classList.remove(...steps);
            play();
          }, at[at.length - 1] + loop),
        );
      }
    };
    play();
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      clear();
    };
  }, [ref, running, at, loop]);
}

/** A scene that keeps working while it is watched: returns a tick that
 *  advances every `ms` while `ref` is in view and rests at `count - 1`, and
 *  pauses (without resetting) when it leaves. With reduced motion it answers
 *  `count - 1`, the scene's settled state, and never moves. The setter moves
 *  it to any beat, from which it carries on. */
export function useTicker<T extends HTMLElement>(
  ref: RefObject<T>,
  ms: number,
  count: number,
): [number, (beat: number) => void] {
  const [tick, setTick] = useState(() => (reducedMotion() ? count - 1 : 0));
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  useEffect(() => {
    if (reducedMotion() || !visible || tick >= count - 1) return;
    const t = window.setTimeout(() => setTick((k) => Math.min(k + 1, count - 1)), ms);
    return () => window.clearTimeout(t);
  }, [visible, tick, ms, count]);
  return [tick, setTick];
}

/** What a scene's player shows and does. */
export type Player = {
  playing: boolean;
  done: boolean;
  step: number;
  toggle: () => void;
  seek: (step: number) => void;
};

/** A timeline the visitor can control, like a video.
 *
 *  Same step classes as `useTimeline`, but driven by a clock the visitor can
 *  pause, resume, replay or move to a chapter: `seek(i)` jumps to the moment
 *  step `i + 1` starts. `bar` receives the progress as `--prog` (0 to 1) on
 *  every frame, so the bar moves without re-rendering; `shape` maps elapsed
 *  milliseconds to that 0 to 1, for a bar whose chapters are not
 *  proportional to their length. It plays from the start whenever `active`
 *  turns on and rests when it turns off. With reduced motion it starts at the
 *  end, and seeking still works. */
export function usePlayer<T extends HTMLElement>(
  ref: RefObject<T>,
  at: number[],
  total: number,
  active: boolean,
  bar: RefObject<HTMLElement>,
  shape?: (ms: number) => number,
): Player {
  const [playing, setPlaying] = useState(false);
  const [step, setStep] = useState(0);
  const elapsed = useRef(0);
  /* Read through a ref so a shape passed inline does not restart the player
     on every render. */
  const shapeRef = useRef(shape);
  shapeRef.current = shape;

  const apply = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const k = at.filter((ms) => elapsed.current >= ms).length;
    at.forEach((_, i) => el.classList.toggle(`s${i + 1}`, i < k));
    const prog = shapeRef.current ? shapeRef.current(elapsed.current) : elapsed.current / total;
    bar.current?.style.setProperty("--prog", prog.toFixed(4));
    setStep(k);
  }, [ref, at, total, bar]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!active) {
      setPlaying(false);
      elapsed.current = 0;
      el.classList.remove("on");
      apply();
      return;
    }
    el.classList.add("on");
    elapsed.current = reducedMotion() ? total : 0;
    setPlaying(!reducedMotion());
    apply();
  }, [ref, active, total, apply]);

  /* Paused means everything stops, including a move already under way. */
  useEffect(() => {
    ref.current?.classList.toggle("paused", active && !playing && elapsed.current < total);
  }, [ref, active, playing, total, step]);

  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    let last: number | undefined;
    const tick = (now: number) => {
      /* A frame after the tab was hidden advances by one frame, not by the
         whole time away: like a video, the scene waits for the visitor. */
      const dt = last === undefined ? 0 : Math.min(Math.max(0, now - last), 100);
      last = now;
      elapsed.current = Math.min(total, elapsed.current + dt);
      apply();
      if (elapsed.current >= total) {
        setPlaying(false);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, total, apply]);

  const toggle = useCallback(() => {
    if (elapsed.current >= total) {
      elapsed.current = 0;
      apply();
      setPlaying(true);
      return;
    }
    setPlaying((p) => !p);
  }, [total, apply]);

  const seek = useCallback(
    (i: number) => {
      elapsed.current = at[i] ?? 0;
      apply();
    },
    [at, apply],
  );

  return { playing, done: !playing && elapsed.current >= total, step, toggle, seek };
}

/** A rectangle in a stage's own coordinates. */
type Box = { x: number; y: number; w: number; h: number };

/** Where `el` sits inside `root`, summed from layout offsets so that the
 *  transforms a scene is applying at the moment of measuring never leak in. */
function boxIn(el: HTMLElement, root: HTMLElement): Box {
  let x = 0;
  let y = 0;
  let n: HTMLElement | null = el;
  while (n && n !== root) {
    x += n.offsetLeft;
    y += n.offsetTop;
    n = n.offsetParent as HTMLElement | null;
  }
  return { x, y, w: el.offsetWidth, h: el.offsetHeight };
}

/** Writes `b` onto `el` as `--<prefix>x/y/w/h`; nothing when there is no box. */
function putBox(el: HTMLElement, prefix: string, b: Box | undefined): void {
  if (!b) return;
  el.style.setProperty(`--${prefix}x`, String(b.x));
  el.style.setProperty(`--${prefix}y`, String(b.y));
  el.style.setProperty(`--${prefix}w`, String(b.w));
  el.style.setProperty(`--${prefix}h`, String(b.h));
}

/** Measures the anchors of every stage (`[data-canvas]`) inside `ref`.
 *
 *  An element marked `data-anchor="id"` is a place something can travel from
 *  or to. `data-fly="from>to"` receives both ends as `--s*` and `--t*`, so CSS
 *  can move one element exactly between two others. Measured again on resize
 *  and once the fonts arrive. */
export function useAnchors<T extends HTMLElement>(ref: RefObject<T>): void {
  useEffect(() => {
    const scope = ref.current;
    if (!scope) return;
    const measure = () => {
      scope.querySelectorAll<HTMLElement>("[data-canvas]").forEach((root) => {
        const boxes = new Map<string, Box>();
        root.querySelectorAll<HTMLElement>("[data-anchor]").forEach((a) => {
          if (a.offsetParent) boxes.set(a.dataset.anchor ?? "", boxIn(a, root));
        });
        root.querySelectorAll<HTMLElement>("[data-fly]").forEach((el) => {
          const [from, to] = (el.dataset.fly ?? "").split(">");
          putBox(el, "s", boxes.get(from));
          putBox(el, "t", boxes.get(to));
        });
      });
      window.dispatchEvent(new Event("scroll"));
    };
    measure();
    void document.fonts.ready.then(measure);
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [ref]);
}

/** Eases in and out, for a camera that starts and stops like a hand would. */
export function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
